import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ExpenseCsvModal from "./modals/ExpenseCsvModal";
import ChangePasswordModal from "./modals/ChangePasswordModal";
import "bootstrap/js/dist/offcanvas";
import api from "../utils/api";

export default function NavMenu({ inSidebar }) {
    const navigate = useNavigate();
    const location = useLocation();

    // derive userRole from token so NavMenu re-renders on location change
    const token = localStorage.getItem("token");
    let userRole;
    try {
        userRole = token ? JSON.parse(atob(token.split(".")[1])).role : null;
    } catch {
        userRole = null;
    }
    const [showChangePw, setShowChangePw] = React.useState(false);

    const handleLogout = async () => {
        try {
            await api.post('/api/auth/logout');
        } catch (e) {
            console.error("Logout error", e);
        }
        localStorage.removeItem("token");
        localStorage.removeItem("mustResetPassword");
        navigate("/login", { replace: true });
    };

    const handleRole = () => {
        if (userRole === "admin" || userRole === "premium") {
            return userRole;
        } else {
            return null;
        }
    };

    const roleBadge = (() => {
        const role = handleRole();
        return role !== null ? (
            <span className="badge bg-dark rounded-pill badge-role ms-2">
                {role}
            </span>
        ) : null;
    })();

    const NavLinks = () => (
        <>
            <ul className="nav nav-pills flex-column mb-auto">
                {userRole ? (
                    <>
                        <li className="nav-item">
                            <button
                                className={`nav-link text-start w-100 ${location.pathname.startsWith("/dashboard") ? "active" : ""}`}
                                onClick={() => navigate("/dashboard")}
                                data-bs-dismiss={!inSidebar ? "offcanvas" : undefined}
                            >
                                <i className="bi bi-speedometer2 me-3"></i>Dashboard
                            </button>
                        </li>
                        <li className="nav-item mt-2">
                            <button
                                className={`nav-link text-start w-100 ${location.pathname.startsWith("/expenses") ? "active" : ""}`}
                                onClick={() => {
                                    const parts = location.pathname.split("/");
                                    const vid = (parts[1] === "dashboard" || parts[1] === "expenses") ? parts[2] : null;
                                    navigate(vid ? `/expenses/${vid}` : "/expenses");
                                }}
                                data-bs-dismiss={!inSidebar ? "offcanvas" : undefined}
                            >
                                <i className="bi bi-receipt me-3"></i>Expenses
                            </button>
                        </li>
                        <li className="nav-item mt-2">
                            <button
                                className={`nav-link text-start w-100 ${location.pathname.startsWith("/manage-vehicles") ? "active" : ""}`}
                                onClick={() => navigate("/manage-vehicles")}
                                data-bs-dismiss={!inSidebar ? "offcanvas" : undefined}
                            >
                                <i className="bi bi-car-front-fill me-3"></i>Manage Vehicles
                            </button>
                        </li>
                        {location.pathname.startsWith("/dashboard") && (
                            <li className="nav-item mt-2">
                                <button
                                    className="nav-link text-start w-100"
                                    onClick={() => navigate(`${location.pathname}?import=fuelly`)}
                                    data-bs-dismiss={!inSidebar ? "offcanvas" : undefined}
                                >
                                    <i className="bi bi-file-earmark-arrow-down me-3"></i>
                                    Import from Fuelly
                                </button>
                            </li>
                        )}
                        {location.pathname.startsWith("/dashboard") && (
                            <li className="nav-item mt-2">
                                <button
                                    className="nav-link text-start w-100"
                                    onClick={() => navigate(`${location.pathname}?import=csv`)}
                                    data-bs-dismiss={!inSidebar ? "offcanvas" : undefined}
                                >
                                    <i className="bi bi-file-earmark-arrow-down me-3"></i>
                                    Import/Export CSV
                                </button>
                            </li>
                        )}
                        {userRole === "admin" && (
                            <li className="nav-item mt-2">
                                <button
                                    className={`nav-link text-start w-100 ${location.pathname.startsWith("/admin") ? "active" : ""}`}
                                    onClick={() => navigate("/admin")}
                                    data-bs-dismiss={!inSidebar ? "offcanvas" : undefined}
                                >
                                    <i className="bi bi-gear-fill me-3"></i>Admin Panel
                                </button>
                            </li>
                        )}
                        <li className="nav-item mt-2">
                            <button
                                className="nav-link text-start w-100"
                                onClick={() => setShowChangePw(true)}
                                data-bs-dismiss={!inSidebar ? "offcanvas" : undefined}
                            >
                                <i className="bi bi-key me-3"></i>Change Password
                            </button>
                        </li>
                    </>
                ) : (
                    <li className="nav-item">
                        <button
                            className="nav-link text-start w-100"
                            onClick={() => navigate("/login")}
                            data-bs-dismiss={!inSidebar ? "offcanvas" : undefined}
                        >
                            <i className="bi bi-box-arrow-in-right me-3"></i>Login
                        </button>
                    </li>
                )}
            </ul>

            <div className="mt-4">
                {userRole && (
                    <>
                        <hr />
                        <button className="btn btn-danger w-100 text-start" onClick={handleLogout}>
                            <i className="bi bi-box-arrow-right me-3"></i>Logout
                        </button>
                    </>
                )}
            </div>
        </>
    );

    const CoreBrand = () => (
        <div className="d-flex align-items-center mb-4 pb-3 border-bottom border-light">
            <h4 className="mb-0 fw-bold">
                <i className="bi bi-speedometer2 me-2 text-primary"></i>FuelScope
            </h4>
            {roleBadge}
        </div>
    );

    return (
        <>
            {/* Expense CSV Modal integration */}
            {location.pathname.startsWith("/dashboard/") && location.search.includes("import=csv") && (
                <ExpenseCsvModal
                    show={true}
                    onClose={() => {
                        const url = location.pathname;
                        navigate(url, { replace: true });
                    }}
                    vehicleId={location.pathname.split("/")[2]}
                />
            )}

            <ChangePasswordModal show={showChangePw} onHide={() => setShowChangePw(false)} />

            {inSidebar ? (
                // ----------------------------------------------------
                // DESKTOP: Permanent Sidebar Rendering
                // ----------------------------------------------------
                <div className="d-flex flex-column h-100 p-4">
                    <CoreBrand />
                    <NavLinks />
                </div>
            ) : (
                // ----------------------------------------------------
                // MOBILE: Top Navbar & Offcanvas Menu
                // ----------------------------------------------------
                <>
                    <nav
                        className="navbar sticky-top px-3 py-2"
                        style={{
                            background: 'rgba(255,255,255,0.75)',
                            backdropFilter: 'blur(24px)',
                            WebkitBackdropFilter: 'blur(24px)',
                            borderBottom: '1px solid rgba(255,255,255,0.8)',
                            boxShadow: '0 4px 24px rgba(0,30,64,0.07)'
                        }}
                    >
                        <div className="container-fluid p-0">
                            <div className="d-flex align-items-center">
                                <button
                                    className="btn"
                                    type="button"
                                    data-bs-toggle="offcanvas"
                                    data-bs-target="#sidebar"
                                    aria-controls="sidebar"
                                    style={{
                                        background: 'linear-gradient(160deg, #001e40 0%, #003366 100%)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '0.75rem',
                                        minHeight: '40px',
                                        padding: '6px 14px'
                                    }}
                                >
                                    <i className="bi bi-list fs-5"></i>
                                </button>
                                <span className="navbar-brand mb-0 h4 ms-3 fw-bold" style={{ color: '#001e40' }}>
                                    <i className="bi bi-speedometer2 me-1" style={{ color: '#001e40' }}></i>FuelScope
                                </span>
                            </div>
                        </div>
                    </nav>

                    <div
                        className="offcanvas offcanvas-start"
                        tabIndex="-1"
                        id="sidebar"
                        data-bs-backdrop="static"
                        aria-labelledby="sidebarLabel"
                    >
                        <div className="offcanvas-header pb-0 border-0">
                            <h5 className="offcanvas-title d-flex align-items-center" id="sidebarLabel">
                                <i className="bi bi-speedometer2 me-2 text-primary"></i>FuelScope
                                {roleBadge}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="offcanvas"
                                aria-label="Close"
                            />
                        </div>
                        <div className="offcanvas-body d-flex flex-column justify-content-between pt-3">
                            <NavLinks />
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
